/**
 * Apple Sign In for Web — client-side helper.
 *
 * Uses the Apple JS SDK loaded from cdn.apple.com to render the Sign in with Apple
 * button and obtain the authorization result (code + id_token) via popup.
 *
 * The heavy lifting (id_token verification) happens on the backend at POST /auth/apple.
 */

export interface AppleAuthResponse {
  code: string;
  id_token: string;
  state?: string;
  user?: {
    name?: { firstName?: string; lastName?: string };
    email?: string;
  };
}

export interface AppleAuthOptions {
  clientId: string;
  redirectURI: string;
  scope?: string;
  state?: string;
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state?: string;
          usePopup?: boolean;
        }) => void;
        signIn: () => Promise<AppleAuthResponse>;
      };
    };
  }
}

const APPLE_SDK_URL = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
let sdkLoaded = false;

function loadAppleSDK(): Promise<void> {
  if (sdkLoaded || typeof window === "undefined") return Promise.resolve();
  if (document.querySelector(`script[src="${APPLE_SDK_URL}"]`)) {
    sdkLoaded = true;
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = APPLE_SDK_URL;
    script.async = true;
    script.onload = () => {
      sdkLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error("Failed to load Apple JS SDK"));
    document.head.appendChild(script);
  });
}

export async function initAppleAuth(options: AppleAuthOptions): Promise<void> {
  await loadAppleSDK();

  if (!window.AppleID) {
    throw new Error("Apple JS SDK not available after loading");
  }

  window.AppleID.auth.init({
    clientId: options.clientId,
    scope: options.scope ?? "name email",
    redirectURI: options.redirectURI,
    state: options.state ?? crypto.randomUUID(),
    usePopup: true,
  });
}

export async function signInWithApple(): Promise<AppleAuthResponse> {
  if (!window.AppleID) {
    throw new Error("Apple Sign In not initialized — call initAppleAuth first");
  }

  const response = await window.AppleID.auth.signIn();
  if (!response?.id_token) {
    throw new Error("Apple Sign In did not return an id_token");
  }

  return response;
}
